import React, { useRef } from 'react';
import { variableColumnsTemplate } from '../../../utils/variableColumnWidths';
import AddVariableMenu from './components/AddVariableMenu';
import VariablesTableBody from './components/VariablesTableBody';
import VariablesTableConfirms from './components/VariablesTableConfirms';
import VariablesTableHeader from './components/VariablesTableHeader';
import useCampaignDocOverlay from './hooks/useCampaignDocOverlay';
import useColumnResize from './hooks/useColumnResize';
import useEditableOrder from './hooks/useEditableOrder';
import useInheritedVariableEdit from './hooks/useInheritedVariableEdit';
import useScopedVariableData from './hooks/useScopedVariableData';
import useStickyTableHeader from './hooks/useStickyTableHeader';
import useVariableMutations from './hooks/useVariableMutations';
import useVariableRename from './hooks/useVariableRename';
import useVariableTableRows from './hooks/useVariableTableRows';
import usePathSlashAutocorrect from './hooks/usePathSlashAutocorrect';
import useVariableTypeChange from './hooks/useVariableTypeChange';
import './styles/layout.css';
import './styles/cells.css';
import './styles/scope.css';
import './styles/actions.css';
import './styles/resize.css';
import './styles/nested.css';

/**
 * Flat variables list — inherited ancestor rows + editable doc/campaign rows.
 * Inherited values can be edited (with confirm) when onPatchInheritedVariable
 * is provided. Cells commit on blur / Enter only (not per keystroke).
 */
const VariablesTable = ({
  /** When set, render only this variable's row (linked-variable-row block). */
  singleVariableId = null,
  docVariables = null,
  onDocVariablesChange = null,
  /**
   * Optional rename hook (DocEditor): (scope, { id, oldName, newName }) => bool.
   * When provided for doc scope, owns the commit (blocks + vars + toast).
   */
  onRenameVariable = null,
  /** Bumped by DocEditor undo/redo so focused cells adopt restored values. */
  historyEpoch = 0,
  campaign = null,
  onCampaignVariablesChange = null,
  /** This mission's value overlays. Present (even []) only inside a mission doc. */
  missionVariables = null,
  onMissionVariablesChange = null,
  /** (sourceSprintId, variableId, patch) — edit ancestor-owned inherited rows. */
  onPatchInheritedVariable = null,
  allSprints = [],
  columnWidths = null,
  onColumnWidthsChange = null,
}) => {
  const {
    newRowIdRef,
    hasDocScope,
    hasCampaignScope,
    hasMissionScope,
    docRows,
    campaignRows,
    displayCampaignRows,
    docRowsRef,
    campaignRowsRef,
    missionRowsRef,
    onDocRef,
    onCampRef,
    onMissionRef,
    onRenameVariableRef,
  } = useVariableTableRows({
    docVariables,
    onDocVariablesChange,
    onRenameVariable,
    historyEpoch,
    campaign,
    onCampaignVariablesChange,
    missionVariables,
    onMissionVariablesChange,
  });

  const {
    inheritedRows,
    higherScopeNames,
    showScopeSelect,
    scopeOptions,
    resolveVariable,
    pickerVariables,
  } = useScopedVariableData({
    hasDocScope,
    hasCampaignScope,
    docVariables,
    docRows,
    campaignRows,
    campaign,
    allSprints,
    missionVariables,
    hasMissionScope,
  });

  const {
    sortColumn,
    sortDirection,
    editableEntries,
    sortableIds,
    sensors,
    handleSort,
    handleDragEnd,
  } = useEditableOrder({
    docRows,
    campaignRows: displayCampaignRows,
    scopeOptions,
    hasDocScope,
    hasCampaignScope,
    docRowsRef,
    campaignRowsRef,
    onDocRef,
    onCampRef,
  });

  const {
    onPatch,
    onScopeChange,
    onDelete,
    onToggleLock,
    handleAdd,
    handleCreateVariable,
  } = useVariableMutations({
    hasDocScope,
    inheritedRows,
    docRowsRef,
    campaignRowsRef,
    missionRowsRef,
    onDocRef,
    onCampRef,
    onMissionRef,
    onDocVariablesChange,
    onCampaignVariablesChange,
    newRowIdRef,
    campaign,
    allSprints,
  });

  const {
    pendingRename,
    onRename,
    confirmPendingRename,
    cancelPendingRename,
  } = useVariableRename({
    docRowsRef,
    campaignRowsRef,
    onDocRef,
    onCampRef,
    onRenameVariableRef,
    onPatch,
  });

  const { claimDocVariable, paintEditableRow, conflictFor } =
    useCampaignDocOverlay({
      hasCampaignScope,
      campaign,
      allSprints,
      campaignRows,
      campaignRowsRef,
      onCampRef,
      inheritedRows,
      higherScopeNames,
      missionVariables,
    });

  const inheritedEdit = useInheritedVariableEdit(
    onPatchInheritedVariable,
    inheritedRows,
    hasCampaignScope ? claimDocVariable : null
  );

  const {
    pendingTypeChange,
    setPendingTypeChange,
    applyTypeChange,
    handleTypeChange,
  } = useVariableTypeChange(onPatch);

  const { localWidths, tableRef, canResize, isResizing, startColumnResize } =
    useColumnResize(columnWidths, onColumnWidthsChange);

  const { sentinelRef, headerRef, stuck } = useStickyTableHeader();

  const canEdit = hasDocScope || hasCampaignScope;
  const isSingleRow = !!singleVariableId;
  const visibleInherited = isSingleRow
    ? inheritedRows.filter((v) => v.id === singleVariableId)
    : inheritedRows;
  const visibleEditable = isSingleRow
    ? editableEntries.filter((e) => e.variable.id === singleVariableId)
    : editableEntries;
  const isEmpty =
    !isSingleRow && inheritedRows.length === 0 && editableEntries.length === 0;

  const displayedRef = useRef([]);
  // Slash direction is chosen from every path variable, not the rows this
  // table happens to paint. A linked row renders one variable, but a paste
  // there still has to match the rest of the table.
  displayedRef.current = [
    ...editableEntries.map(({ variable, scope }) => ({
      scope,
      variable: paintEditableRow(variable, scope),
      // Locked rows still have an editable value.
      locked: false,
      writable: true,
    })),
    ...inheritedRows.map((raw) => {
      const variable = inheritedEdit.displayVariable(raw);
      return {
        scope: 'inherited',
        variable,
        locked: true,
        writable: false,
      };
    }),
  ];

  const pathSlash = usePathSlashAutocorrect({
    displayedRef,
    onPatch,
  });

  const undoPathSlash = (offer) => {
    pathSlash.undo(offer, (_offer, patch) => {
      const raw = inheritedRows.find((row) => row.id === _offer.variableId);
      if (raw) inheritedEdit.patchValue(raw, patch);
    });
  };
  const gridTemplate = variableColumnsTemplate(localWidths, {
    includeDrag: !isSingleRow,
  });
  const showAdd = canEdit && !isSingleRow;

  return (
    <>
      <div className={`variables-panel__table-wrap${isResizing ? ' is-col-resizing' : ''}`}>
        <div
          className={`variables-panel__table${isSingleRow ? ' variables-panel__table--single' : ''}`}
          ref={tableRef}
          style={{ '--vp-grid-cols': gridTemplate }}
        >
          <VariablesTableHeader
            isSingleRow={isSingleRow}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            canResize={canResize}
            startColumnResize={startColumnResize}
            sentinelRef={sentinelRef}
            headerRef={headerRef}
            stuck={stuck}
          />
          <VariablesTableBody
            isEmpty={isEmpty}
            isSingleRow={isSingleRow}
            visibleInherited={visibleInherited}
            visibleEditable={visibleEditable}
            inheritedEdit={inheritedEdit}
            paintEditableRow={paintEditableRow}
            conflictFor={conflictFor}
            showScopeSelect={showScopeSelect}
            scopeOptions={scopeOptions}
            onPatch={pathSlash.onPatch}
            onRename={onRename}
            onScopeChange={onScopeChange}
            onTypeChange={handleTypeChange}
            onDelete={onDelete}
            onToggleLock={onToggleLock}
            resolveVariable={resolveVariable}
            pickerVariables={pickerVariables}
            onCreateVariable={handleCreateVariable}
            historyEpoch={historyEpoch + pathSlash.paintEpoch}
            pathSlashOffers={pathSlash.offers}
            onUndoPathSlash={undoPathSlash}
            commitPathSlash={pathSlash.commitThrough}
            missionVariables={missionVariables}
            newRowIdRef={newRowIdRef}
            sensors={sensors}
            sortableIds={sortableIds}
            handleDragEnd={handleDragEnd}
          />
        </div>

        {showAdd && <AddVariableMenu onAdd={handleAdd} />}
      </div>

      <VariablesTableConfirms
        pendingRename={pendingRename}
        confirmPendingRename={confirmPendingRename}
        cancelPendingRename={cancelPendingRename}
        inheritedEdit={inheritedEdit}
        hasCampaignScope={hasCampaignScope}
        campaign={campaign}
        pendingTypeChange={pendingTypeChange}
        applyTypeChange={applyTypeChange}
        setPendingTypeChange={setPendingTypeChange}
      />
    </>
  );
};

export default VariablesTable;
